import { useEffect, useMemo, useState } from "react";
import { Loading } from "@/components/Loading";
import { Dash } from "@/components/Dash";
import { Link, useNavigate } from "react-router-dom";
import { useBackOrigin, useRememberListUrl } from "@/hooks/useBackTo";
import { LIST_PARAMS, useUrlParam } from "@/hooks/useUrlParam";
import { useRestoreListScroll } from "@/hooks/useListScroll";
import { SortableTH, sortRows, type SortDirection } from "@/components/SortableTH";
import { SearchInput } from "@/components/SearchInput";
import { Pagination } from "@/components/Pagination";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { StatusPill, ROLE_TYPE_TONES } from "@/components/StatusPill";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DEFAULT_PAGE_SIZE } from "@/lib/paging";
import { ApiError } from "@/lib/apiClient";
import { roles as rolesApi, type RoleSummary } from "@/features/users/api/users.api";

const SORT_KEYS = ["name", "permissionCount"] as const;
type SortKey = (typeof SORT_KEYS)[number];
const SORT_DIRECTIONS: readonly SortDirection[] = ["asc", "desc"];

export default function RolesPage() {
  const navigate = useNavigate();
  const origin = useBackOrigin();
  useRememberListUrl();
  const [rows, setRows] = useState<RoleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  useRestoreListScroll(rows !== null);
  const [keyword, setKeyword] = useUrlParam(LIST_PARAMS.keyword, "");
  const [pageParam, setPageParam] = useUrlParam(LIST_PARAMS.page, "1");
  const page = Math.max(1, Number.parseInt(pageParam, 10) || 1);
  const setPage = (p: number) => setPageParam(String(Math.max(1, p)));

  useEffect(() => {
    let cancelled = false;
    rolesApi.list()
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load roles.");
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const all = rows ?? [];
    const q = keyword.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) =>
      r.name.toLowerCase().includes(q) || (r.description ?? "").toLowerCase().includes(q),
    );
  }, [rows, keyword]);

  const [sortKey, setSortKey] = useUrlParam<SortKey>(LIST_PARAMS.sortBy, "name", SORT_KEYS);
  const [direction, setDirection] = useUrlParam<SortDirection>(LIST_PARAMS.sortDir, "asc", SORT_DIRECTIONS);
  const sortedRows = useMemo(() => sortRows(filtered, sortKey, direction), [filtered, sortKey, direction]);
  const setSort = (key: SortKey) => {
    setDirection(key === sortKey && direction === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const pageSize = DEFAULT_PAGE_SIZE;
  const totalCount = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const paged = {
    pageNumber: currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasPreviousPage: currentPage > 1,
    hasNextPage: currentPage < totalPages,
  };

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Roles</h1>
          <p className="text-sm text-slate-500 mt-1">
            Roles bundle permissions. Assign a role to an user via
            Manage → Users. System roles are reserved seeds and can't be
            deleted.
          </p>
        </div>
        <Button asChild className="self-start">
          <Link to="/manage/roles/new" state={origin}>New role</Link>
        </Button>
      </header>

      {forbidden && (
        <Notice tone="warn">You don't have permission to view roles (code 70.10).</Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && rows === null && (
        <Loading>Loading roles…</Loading>
      )}

      {!forbidden && !error && rows !== null && rows.length === 0 && (
        <Notice tone="info">
          No roles yet. Create the first one with the New role button —
          assign permission 70.05 to let users see the Users list, 20.05
          for Merchants, and so on.
        </Notice>
      )}

      {!forbidden && !error && rows !== null && rows.length > 0 && (
        <>
          <div className="mb-3 flex">
            <SearchInput
              value={keyword}
              onSearch={setKeyword}
              placeholder="Search by name or description…"
            />
          </div>

          {totalCount === 0 ? (
            <Notice tone="info">No roles match your search.</Notice>
          ) : (
            <div className="rounded-md border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTH sortKey="name" current={sortKey} direction={direction} onClick={setSort}>Name</SortableTH>
                    <SortableTH sortKey="permissionCount" current={sortKey} direction={direction} onClick={setSort} align="right">Permissions</SortableTH>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Description</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/manage/roles/${r.id}`, { state: origin })}
                    >
                      <TableCell className="font-medium">
                        <Link to={`/manage/roles/${r.id}`} state={origin} className="text-slate-900 hover:underline">
                          {r.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.permissionCount}</TableCell>
                      <TableCell className="text-slate-700">
                        {r.description ?? <Dash />}
                      </TableCell>
                      <TableCell>
                        <StatusPill value={r.isSystem ? "System" : "User"} map={ROLE_TYPE_TONES} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={paged} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </>
  );
}
